import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
// Removed unused complex validation imports

const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [lockoutInfo, setLockoutInfo] = useState<{
    isLocked: boolean;
    remainingMinutes: number;
  } | null>(null);
  
  // Handle redirect when user logs in successfully
  useEffect(() => {
    if (user) {
      console.log('🚀 User detected, redirecting...');
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  // Handle email confirmation and success messages
  useEffect(() => {
    const confirmed = searchParams.get('confirmed');
    if (confirmed === 'true') {
      setSuccess('Email confirmed successfully! You can now sign in.');
    }
    
    // Handle messages from location state (e.g., from password update)
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the state to prevent showing the message on refresh
      window.history.replaceState(null, '');
    }
  }, [searchParams, location.state]);

  // Simple input handlers
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError(''); // Clear error when user types
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (passwordError) setPasswordError(''); // Clear error when user types
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Form submit started');
    
    // Clear previous messages
    setError('');
    setSuccess('');
    setEmailError('');
    setPasswordError('');
    setLockoutInfo(null);

    // Simple validation
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }
    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    console.log('Starting login process...');

    try {
      const result = await login(email, password);
      console.log('Login process completed, result:', result);
      
      if (result.success) {
        console.log('Login successful - redirecting immediately');
        // Success - auth context will handle redirect, no loading state needed
      } else {
        console.log('Login failed, showing error immediately');
        setError('Either email or password is wrong. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-black dark:via-gray-900 dark:to-gray-800 py-4 sm:py-8 lg:py-12 px-3 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/" className="inline-flex items-center space-x-1 sm:space-x-2 mb-4 sm:mb-6">
            <img src="/apple-touch-icon.png" alt="EightyThreeLabs" className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10" />
            <span className="text-lg sm:text-xl lg:text-2xl font-semibold">
              <span className="text-brand-primary">EightyThree</span>
              <span className="text-black dark:text-white">Labs</span>
            </span>
          </Link>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Welcome back! Let's continue your journey.
          </p>
        </div>

        {/* Main Form Card */}
        <div className="bg-white dark:bg-gray-900 py-6 sm:py-8 px-4 sm:px-6 shadow-xl rounded-xl border border-gray-100 dark:border-gray-700">
          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {lockoutInfo?.isLocked ? (
                    <span className="text-red-500 text-lg">🔒</span>
                  ) : error.includes('Email verification') ? (
                    <span className="text-red-500 text-lg">📧</span>
                  ) : (
                    <span className="text-red-500 text-lg">⚠️</span>
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  {lockoutInfo?.isLocked && (
                    <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                      For security, try again in {lockoutInfo.remainingMinutes} minute{lockoutInfo.remainingMinutes !== 1 ? 's' : ''}.
                    </p>
                  )}
                  {error.includes('Email verification') && (
                    <div className="mt-2 text-xs text-red-500 dark:text-red-400">
                      <p>• Check your email inbox and spam folder</p>
                      <p>• Click the verification link in the email</p>
                      <p>• If no email received, you may need to sign up again</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                required
                className={`w-full px-3 py-2 border rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                  emailError 
                    ? 'border-red-300 dark:border-red-600 focus:ring-red-500' 
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                }`}
                placeholder="Enter your email"
              />
              {emailError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{emailError}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
                className={`w-full px-3 py-2 border rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${
                  passwordError 
                    ? 'border-red-300 dark:border-red-600 focus:ring-red-500' 
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                }`}
                placeholder="Enter your password"
              />
              {passwordError && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{passwordError}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={lockoutInfo?.isLocked}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {lockoutInfo?.isLocked ? (
                `🔒 Account Locked (${lockoutInfo.remainingMinutes}m remaining)`
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Forgot Password and Signup Links */}
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm">
              <Link to="/reset-password" className="font-medium text-blue-600 hover:text-blue-500">
                Forgot your password?
              </Link>
            </div>
            <div className="text-sm">
              <Link
                to="/signup"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Don't have an account?
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;